import { record, scalar, upper, type Row } from '../ability/context.js';
import { removeStatusFromRow, statusItemId, statusRows, type StatusRemoval } from '../statuses/expiry.js';
import type { SkillAdvPatch } from '../actions/self-status.js';
import { STATUS_ATTACK_BUDGET_KEYS, type StatusAttackBudget } from '../compile/status-attack-budget.js';
import { statusRemovalRuntime, applyStatusRemovalRuntime } from '../compile/status-removal-runtime.js';
import { removeStatusPassiveGrants } from '../compile/status-runtime-passives.js';

export interface EndTurnRowFrame { status_remove_ids: string[]; skills: Record<string, SkillAdvPatch>; defenses?: Row; attack_budget?: StatusAttackBudget }
const int = (value: unknown): number => { const n = Number(scalar(value)); return Number.isFinite(n) ? Math.trunc(n) : 0; };

/** Reverse all removed status contributions together, using the compiled skill's source lines. */
export function skillsWithoutStatuses(row: Row, statusIds: readonly string[]): { skills: Record<string, SkillAdvPatch>; netZeroWithLines: string[] } {
  const remove = new Set(statusIds.map(upper));
  const skills: Record<string, SkillAdvPatch> = {};
  const netZeroWithLines: string[] = [];
  const cluster = record(record(record(row.computed)?.creature)?.skills) ?? {};
  for (const [variable, entry] of Object.entries(cluster)) {
    const skill = record(entry), breakdown = record(skill?.adv_dis_breakdown);
    if (!skill || !Array.isArray(breakdown?.lines)) continue;
    const lines = (breakdown.lines as Row[]).filter(line => !remove.has(upper(line.href_id))).map(line => ({ ...line }));
    if (lines.length === breakdown.lines.length) continue;
    const stacks = lines.reduce((sum, line) => sum + int(line.value), 0);
    if (stacks === 0 && lines.length > 0) netZeroWithLines.push(variable);
    skills[variable] = { adv_dis_stacks: stacks, adv_dis_breakdown: { lines } };
  }
  return { skills, netZeroWithLines };
}
export function skillsWithoutStatus(row: Row, statusId: string): Record<string, SkillAdvPatch> { return skillsWithoutStatuses(row, [statusId]).skills; }

/** A caster projection loses the row too, while only its target loses compiled contributions. */
export function statusRemovalFrame(row: Row | null, removals: readonly StatusRemoval[], casterOnly: readonly StatusRemoval[] = []): EndTurnRowFrame {
  const defenses = [...removals].reverse().find(r => r.defenses !== undefined)?.defenses;
  const attack_budget = [...removals].reverse().find(r => r.attack_budget !== undefined)?.attack_budget;
  return { status_remove_ids: [...new Set([...removals, ...casterOnly].map(r => r.status_id))],
    skills: row ? skillsWithoutStatuses(row, removals.map(r => r.status_id)).skills : {},
    ...(defenses !== undefined ? { defenses } : {}), ...(attack_budget ? { attack_budget } : {}) };
}

/** One projection path for turn frames, time frames and disposable native-round state. */
export function applyStatusFrame(row: Row, part: EndTurnRowFrame, definition?: (id: string) => Row | null): void {
  const held = new Map(statusRows(row, 'target').map(s => [statusItemId(s), record(s.status_arr)]));
  const runtime = statusRemovalRuntime(row, part.status_remove_ids,
    id => definition?.(id) ?? record(record(row.catalog)?.[id]) ?? held.get(id) ?? null);
  if (!runtime.ok) throw new Error('status-compiled-footprint:' + runtime.layer);
  removeStatusPassiveGrants(row, part.status_remove_ids);
  for (const id of part.status_remove_ids) removeStatusFromRow(row, id);
  if (part.defenses !== undefined || part.attack_budget || Object.keys(part.skills ?? {}).length) {
    const computed = (row.computed ??= {}) as Row, creature = (computed.creature ??= {}) as Row;
    if (part.defenses !== undefined) creature.defenses = JSON.parse(JSON.stringify(part.defenses));
    const budget = record(part.attack_budget);
    if (budget) for (const key of STATUS_ATTACK_BUDGET_KEYS) {
      const cluster = record(budget[key]);
      if (cluster) creature[key] = structuredClone(cluster);
    }
    for (const [variable, patch] of Object.entries(part.skills ?? {})) {
      const skills = (creature.skills ??= {}) as Row;
      const existing = record(skills[variable]) ?? {};
      if (patch.adv_dis_stacks === 0 && !patch.adv_dis_breakdown.lines.length) {
        const { adv_dis_stacks: _s, adv_dis_breakdown: _b, ...rest } = existing; void _s; void _b; skills[variable] = rest;
      } else skills[variable] = { ...existing, adv_dis_stacks: patch.adv_dis_stacks,
        adv_dis_breakdown: { lines: patch.adv_dis_breakdown.lines.map(line => ({ ...line })) } };
    }
  }
  applyStatusRemovalRuntime(row, runtime);
}
